/**
 * Who's using the app: name + role. The prototype switches role from Settings
 * ("My role") and the whole board re-filters — Sales sees their own customers,
 * Purchaser their assignments, Purchase Manager everything.
 *
 * Role gating here is for UX only. The server enforces it (contract §1 — e.g. reassign
 * purchaser is manager-only, 403 otherwise); the UI must never be the only check.
 *
 * TWO IDENTITIES, NOT ONE — read this before changing `user` or `role` below.
 *
 * `identity` (email/name) now comes from Clerk when it's configured — that's who signed in to
 * *Bosun*. `user`/`role` stay exactly the old locally-picked stub, because they are NOT a
 * display name: `user` is matched against the `purchaser`/`sales_person` string fields on
 * live `Opportunity` records (see `lib/kanban.ts`'s `scopeForRole`) and written onto new ones
 * — i.e. it stands in for the *Frappe* user, a completely different account system Clerk knows
 * nothing about. Sourcing `user` from Clerk would silently break that matching the moment
 * someone's Clerk name isn't one of the seeded Frappe names ("Priya"/"Akhil"/"Manoj") — the
 * Kanban would just show nothing, with no obvious reason why. Real role names are also still
 * unknown (open question Y3), so there is nothing to map Clerk metadata onto yet regardless.
 * This split holds until the real Frappe session lands — see `lib/auth.ts`'s header for what
 * is and isn't authorised today, and docs/bosun-go-live-checklist.md's E1/F1/F2 for the rest.
 */

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { setActingRole } from '../lib/api/client'
import { effectiveRole } from '../lib/actingRole'
import { useCurrentUser } from '../lib/api/useMasters'
import { setMyMailbox, useMyMailbox } from '../lib/api/mailbox'
import { useClerkIdentity } from './ClerkIdentityBridge'
import type { ClerkIdentity } from '../lib/auth'
import type { Role } from '../lib/types'

/** Sample users from DATA-MODEL.md, keyed by contract role. */
export const ROLE_USERS: Record<Role, string> = {
  sales: 'Priya',
  purchaser: 'Akhil',
  purchase_manager: 'Manoj',
}

export const ROLE_LABELS: Record<Role, string> = {
  sales: 'Sales',
  purchaser: 'Purchaser',
  purchase_manager: 'Purchase Manager',
}

interface SessionContextValue {
  role: Role
  /** The role the server granted — the ceiling for `setRole`; `null` until it answers. */
  grantedRole: Role | null
  roleLabel: string
  user: string
  setRole: (role: Role) => void
  /** Purchaser or manager may accept/reject a quotation (prototype `canDecide`). */
  canDecide: boolean
  isManager: boolean
  /**
   * The signed-in purchaser's own email address, used to filter the shared mailbox down to the
   * threads they are on.
   *
   * Why this exists: enquiries arrive when a salesman forwards a customer mail to ONE common
   * mailbox with the responsible purchaser on Cc. Several purchasers and salesmen share that
   * mailbox, so without a filter every purchaser reads everyone's enquiries. Matching on Cc (and
   * To) is what narrows it — see `involvesAddress` in `lib/mail.ts`.
   *
   * **Session-only, and it resets on reload.** Browser storage is banned project-wide, and there
   * is no server-side per-user preference to write it to yet. The real source is
   * `frappe.auth.get_logged_user`, which returns the session user's email — once the app
   * authenticates with a session rather than a dev token, this stops being something anyone types.
   * Q24 tracks that, and also the point that a client-side filter is a VIEW, never a permission.
   */
  myAddress: string
  setMyAddress: (address: string) => void
  /**
   * Who is signed in to Bosun itself, per Clerk — `null` when Clerk isn't configured (the
   * no-key stub fallback), the Clerk user hasn't loaded yet, or they have no usable email.
   * Deliberately NOT `user` above — see this file's header.
   */
  identity: ClerkIdentity | null
}

const SessionContext = createContext<SessionContextValue | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  /**
   * The server's answer, when we have one. `auth.get_current_user` (Complete API v2) returns the
   * signed-in user's email AND their Bosun role, already mapped from the Frappe roles — which is
   * ask `A5`, the thing this provider used to guess. Until it resolves (or if it fails) we fall
   * back to the locally-chosen role below, so the app is usable offline and against the mock.
   */
  const currentUser = useCurrentUser()

  /*
    The role SHOWN is the granted one unless the person chose a lower one in Settings
    (`lib/actingRole.ts`, 2026-09-21): a manager may look at the purchaser's board — scoped to
    their own enquiries, as a purchaser's is — but nobody acts up. Before the server answers, the
    choice stands, purchaser by default.
  */
  const [chosenRole, setChosenRole] = useState<Role | null>(null)
  const grantedRole = currentUser.data?.role ?? null
  const role = effectiveRole(grantedRole, chosenRole)
  const setRole = setChosenRole
  /**
   * The REAL Frappe `User` docname — an email. This is what `purchaser`/`sales_person` Links want,
   * and sending anything else fails with `User '<x>' not found`.
   *
   * `''` while unresolved, deliberately: a caller must omit the field rather than substitute a
   * name the server will reject. `ROLE_USERS` below is no longer consulted for this — it survives
   * only as seed-data labels for the mock.
   */
  const user = currentUser.data?.email ?? ''

  // Persisted per person (2026-09-18) — see `lib/api/mailbox.ts`; the setter writes through.
  const myAddress = useMyMailbox()
  const setMyAddress = setMyMailbox
  const identity = useClerkIdentity()

  // Mirror the acting role onto the API client so the mock can enforce its role gates.
  // Production reads the role from the session, not from us.
  useEffect(() => {
    setActingRole(role)
  }, [role])

  const value = useMemo<SessionContextValue>(
    () => ({
      role,
      grantedRole,
      roleLabel: ROLE_LABELS[role],
      user,
      setRole,
      canDecide: role === 'purchaser' || role === 'purchase_manager',
      isManager: role === 'purchase_manager',
      myAddress,
      setMyAddress,
      identity,
    }),
    [role, grantedRole, user, myAddress, identity],
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext)
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider>')
  return ctx
}
